/* eslint-disable camelcase */
import {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router';
import i18n from 'i18next';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
  WorkoutResourceType,
} from '@gymcloud/core';
import {
  getBasePath,
  getEntityType,
} from 'web-utils';

import {
  Spinner,
  NotFound,
  Breadcrumbs,
  ContentWrapper,
  NavigationTabs,
  WorkoutTemplate,
} from 'web-components';

import {
  getUiState,
  getFeatures,
} from 'web-selectors';

import {
  uiActions,
} from 'web-actions';
import {
  usePageView,
} from 'web-hooks';

import {
  PrintLogo,
  Flex,
} from 'web-styled';

import AssignContainer from '../AssignContainer';
import AdditionalWorkoutPage from './AdditionalWorkoutPage';

interface Props {
  resourceType: WorkoutResourceType;
}

const WorkoutTemplatesPage = ({ resourceType }: Props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const id = Number(params.id);

  const mapState = useCallback((state) => {
    const workout = coreSelectors.getResourceById(
      resourceType,
      id,
    )(state);
    const workoutAuthor = coreSelectors.getResourceById(
      'clients',
      workout?.author_id,
    )(state);
    return {
      readStatus: getStatus(
        state,
        [
          `${resourceType}.requests.getFullWorkout.status`,
          `${resourceType}.meta[${id}].readStatus`,
        ],
        true,
      ),
      workout,
      workoutAuthor,
      exercises: coreSelectors.getSortedListWorkoutExercises(
        id,
      )(state),
      breadcrumbs: coreSelectors.getBreadcrumbs(
        resourceType,
        id,
      )(state),
      features: getFeatures(state),
      openedTooltips: getUiState('openedTooltips')(state),
    };
  }, [id]);

  const {
    workout,
    features,
    exercises,
    readStatus,
    breadcrumbs,
    workoutAuthor,
    openedTooltips,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestFullWorkout({
        id,
        resourceType,
      }),
    );
  }, [id]);

  usePageView({
    eventKey: 'viewed_workout_template',
    attrs: workout,
    enabled: readStatus.succeeded && workout,
  });

  const basePath = getBasePath(resourceType);
  const label = getEntityType(resourceType);

  const isEdit = (location.pathname || '').indexOf('edit') !== -1;
  const assignTab = exercises.length > 0 ? ['assign'] : [];
  const otherTabs = resourceType === 'workoutTemplates'
    ? ['warmup', 'cooldown', ...assignTab]
    : assignTab;

  const tabs = [
    {
      name: i18n.t('workout'),
      to: `${basePath}/${id}/${isEdit ? 'edit' : 'workout'}`,
    },
    ...otherTabs.map(
      name => ({
        name,
        to: `${basePath}/${id}/${name}`,
      }),
    ),
  ];

  const removeAssignFromTooltips = () => {
    dispatch(
      uiActions.setUiState({
        openedTooltips: openedTooltips.filter(el => el.key !== 'assign_workout'),
      }),
    );
  };

  const handleUpdate = (payload: any, actions: any) => {
    if (openedTooltips?.some(el => el.key === 'edit_workout_button')) {
      const newList = openedTooltips.filter(el => el.key !== 'edit_workout_button');
      dispatch(
        uiActions.setUiState({
          openedTooltips: [...newList, { key: 'add_workout_exercise' }],
        }),
      );
      dispatch(
        coreActions.requestMarkTutotialStepsComplite({
          steps: ['add_exercise'],
        }),
      );
    }
    dispatch(
      coreActions.requestUpdateEntity({
        id,
        payload,
        endNotify: true,
        resourceType,
        callbacks: {
          ...actions,
          onSuccess: () => {
            navigate(`${basePath}/${workout.id}/workout`);
          },
        },
      }),
    );
  };

  const handleDelete = () => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id,
      resourceType,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: i18n.t('ask.delete', { type: `$t(${resourceType})` }),
          bodyText: i18n.t('item.ask.delete', {
            name: workout.name,
          }),
          confirmText: 'Delete',
        },
        onSuccess: () => {
          navigate(`${basePath}_folder/${workout.folder_id}`);
        },
        onConfirmAction,
      }),
    );
  };

  return (
    <ContentWrapper
      header={(
        <Flex
          fillSpace
          align="center"
          justify="space-between"
        >
          <Breadcrumbs
            items={breadcrumbs}
            basePath={basePath}
            backPath={workout?.is_public ? undefined : `${basePath}_folder`}
            entityFolderId={workout?.is_public ? undefined : workout?.folder_id}
          />
          <NavigationTabs
            tabs={tabs}
            openedTooltips={openedTooltips}
            onRemoveTooltip={removeAssignFromTooltips}
          />
          <PrintLogo />
        </Flex>
      )}
    >
      {readStatus.succeeded && (
        <Routes>
          <Route
            index
            element={<Navigate to="workout" />}
          />
          <Route
            path="workout"
            element={(
              <WorkoutTemplate
                readOnly
                type={label}
                fillPath={`${basePath}/${workout.id}`}
                workout={workout}
                workoutAuthor={workoutAuthor}
                resourceType={resourceType}
                openedTooltips={openedTooltips}
                onRequestSubmit={() => { }}
                onRequestDelete={handleDelete}
              />
            )}
          />
          <Route
            path="warmup/*"
            element={(
              <AdditionalWorkoutPage
                id={workout.warmup_id}
                workout={workout}
                basePath={basePath}
                parentType="workoutTemplates"
                resourceType="warmups"
              />
            )}
          />
          <Route
            path="cooldown/*"
            element={(
              <AdditionalWorkoutPage
                id={workout.cooldown_id}
                workout={workout}
                basePath={basePath}
                parentType="workoutTemplates"
                resourceType="cooldowns"
              />
            )}
          />
          <Route
            path="assign"
            element={(
              <AssignContainer
                type="workout"
                templateId={id}
              />
            )}
          />
          <Route
            path="edit"
            element={(
              <WorkoutTemplate
                type={label}
                fillPath={`${basePath}/${workout.id}`}
                workout={workout}
                features={features}
                workoutAuthor={workoutAuthor}
                resourceType={resourceType}
                onRequestSubmit={handleUpdate}
              />
            )}
          />
        </Routes>
      )}

      {readStatus.pending && (
        <Spinner message="Loading template..." />
      )}

      {readStatus.failed && (
        <NotFound title={`Not Found (${id})`} />
      )}
    </ContentWrapper>
  );
};

export default WorkoutTemplatesPage;
